import { useState, useCallback } from 'react';

type StepName = string;

interface Options {
  steps?: StepName[];
  initialStepNumber?: number;
}

interface UseStepsReturn {
  steps: StepName[];
  currentStepName: StepName | undefined;
  visitedSteps: StepName[];
  isValidStepName: (step?: StepName) => boolean;
  isValidStepNumber: (n: number) => boolean;
  goPrev: () => void;
  goNext: () => void;
  goTo: (stepName: StepName) => void;
  getStepIndex: (stepName?: StepName) => number;
  getPrevStepName: () => StepName | undefined;
  getNextStepName: () => StepName | undefined;
  isStepActive: (stepName: StepName) => boolean;
  isStepVisited: (stepName: StepName) => boolean;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
  updateVisitedSteps: () => void;
}

const INITIAL_STEPS: StepName[] = [];
const INITIAL_STEP_NUMBER = 0;

export function useSteps(options: Options = {}): UseStepsReturn {
  const { steps = INITIAL_STEPS, initialStepNumber = INITIAL_STEP_NUMBER } = options;

  const [_steps] = useState<StepName[]>(steps);
  const initialStep = steps?.[initialStepNumber];
  const [currentStepName, setCurrentStepName] = useState<StepName | undefined>(initialStep);
  const [visitedSteps, setVisitedSteps] = useState<StepName[]>(
    initialStep !== undefined ? [initialStep] : [],
  );

  const getStepIndex = useCallback(
    (stepName: StepName | undefined = currentStepName): number =>
      stepName !== undefined ? _steps.indexOf(stepName) : -1,
    [_steps, currentStepName],
  );

  const isValidStepName = useCallback(
    (step?: StepName): boolean => step !== undefined && _steps.includes(step),
    [_steps],
  );

  const isValidStepNumber = useCallback(
    (stepNumber: number): boolean => !!_steps?.[stepNumber],
    [_steps],
  );

  const goToStep = useCallback(
    (step: StepName | undefined): void => {
      if (step === undefined) return;
      setCurrentStepName(step);
      setVisitedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
    },
    [],
  );

  const goPrev = useCallback((): void => {
    const currentStepIndex = getStepIndex();
    const prevStepIndex = currentStepIndex - 1;

    if (!isValidStepNumber(prevStepIndex)) return;

    goToStep(_steps?.[prevStepIndex]);
  }, [_steps, getStepIndex, isValidStepNumber, goToStep]);

  const goNext = useCallback((): void => {
    const currentStepIndex = getStepIndex();
    const nextStepIndex = currentStepIndex + 1;

    if (!isValidStepNumber(nextStepIndex)) return;

    goToStep(_steps?.[nextStepIndex]);
  }, [_steps, getStepIndex, isValidStepNumber, goToStep]);

  const goTo = useCallback(
    (stepName: StepName): void => {
      const stepIndex = getStepIndex(stepName);

      if (!isValidStepNumber(stepIndex)) return;

      goToStep(_steps?.[stepIndex]);
    },
    [_steps, getStepIndex, isValidStepNumber, goToStep],
  );

  const getPrevStepName = useCallback((): StepName | undefined => {
    const stepIndex = getStepIndex(currentStepName);
    const prevStepIndex = stepIndex - 1;

    if (!isValidStepNumber(prevStepIndex)) return undefined;

    return _steps?.[prevStepIndex];
  }, [_steps, currentStepName, isValidStepNumber, getStepIndex]);

  const getNextStepName = useCallback((): StepName | undefined => {
    const stepIndex = getStepIndex(currentStepName);
    const nextStepIndex = stepIndex + 1;

    if (!isValidStepNumber(nextStepIndex)) return undefined;

    return _steps?.[nextStepIndex];
  }, [_steps, currentStepName, isValidStepNumber, getStepIndex]);

  const isStepActive = useCallback(
    (stepName: StepName): boolean => currentStepName === stepName,
    [currentStepName],
  );

  const isStepVisited = useCallback(
    (stepName: StepName): boolean => visitedSteps.includes(stepName),
    [visitedSteps],
  );

  const isFirstStep = useCallback(
    (): boolean => getStepIndex(currentStepName) === 0,
    [currentStepName, getStepIndex],
  );

  const isLastStep = useCallback(
    (): boolean => getStepIndex(currentStepName) === _steps?.length - 1,
    [currentStepName, _steps?.length, getStepIndex],
  );

  const updateVisitedSteps = useCallback((): void => {
    if (currentStepName === undefined) return;
    setVisitedSteps((prev) =>
      prev.includes(currentStepName) ? prev : [...prev, currentStepName],
    );
  }, [currentStepName]);

  return {
    steps: _steps,
    currentStepName,
    visitedSteps,
    isValidStepName,
    isValidStepNumber,
    goPrev,
    goNext,
    goTo,
    getStepIndex,
    getPrevStepName,
    getNextStepName,
    isStepActive,
    isStepVisited,
    isFirstStep,
    isLastStep,
    updateVisitedSteps,
  };
}

export default useSteps;
